// src/pages/ResetPassword.jsx
"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/DesarrollandoTerritorios.png"
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // "success" | "error" | null
  const [message, setMessage] = useState("");

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setMessage("");

    if (!password || !confirmPassword) {
      setStatus("error");
      setMessage("Debes ingresar y confirmar la nueva contraseña.");
      return;
    }

    if (password.length < 8) {
      setStatus("error");
      setMessage("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    if (password !== confirmPassword) {
      setStatus("error");
      setMessage("Las contraseñas no coinciden.");
      return;
    }

    try {
      setLoading(true);

      // 🔹 Aquí podrías llamar a tu API para cambiar la contraseña
      // Por ahora solo simulamos éxito
      await new Promise((resolve) => setTimeout(resolve, 800));

      setStatus("success");
      setMessage("Contraseña actualizada correctamente. Ahora puedes iniciar sesión.");
    } catch (error) {
      console.error("Error al restablecer contraseña:", error);
      setStatus("error");
      setMessage("Ocurrió un error al restablecer la contraseña. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const goBackToLogin = () => {
    navigate("/login");
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        alignItems: "center",
        background: "linear-gradient(to bottom, #1e3a8a, rgb(199, 204, 223))",
      }}
    >
      <div
        style={{
          display: "flex",
          flex: 1,
          width: "100%",
          justifyContent: "center",
          alignItems: "center",
          padding: "2rem",
        }}
      >
        <div
          style={{
            maxWidth: "420px",
            width: "100%",
            backgroundColor: "white",
            borderRadius: "1rem",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              padding: "1.5rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <img
              src={logo || "/placeholder.svg"}
              alt="Logo"
              style={{ height: "160px", width: "auto", marginBottom: "0.75rem" }}
            />

            <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", textAlign: "center", marginBottom: "0.5rem" }}>
              Restablecer contraseña
            </h2>
            <p style={{ fontSize: "0.9rem", color: "#6b7280", textAlign: "center", marginBottom: "1rem" }}>
              Ingrese su nueva contraseña y confírmela para actualizar su acceso al sistema.
            </p>

            {status === "error" && (
              <div
                style={{
                  backgroundColor: "#fef2f2",
                  color: "#b91c1c",
                  padding: "0.75rem",
                  borderRadius: "0.5rem",
                  marginBottom: "1rem",
                  width: "100%",
                  textAlign: "center",
                }}
              >
                <p style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <AlertCircle size={18} style={{ marginRight: "0.5rem" }} />
                  {message}
                </p>
              </div>
            )}

            {status === "success" && (
              <div
                style={{
                  backgroundColor: "#ecfdf3",
                  color: "#166534",
                  padding: "0.75rem",
                  borderRadius: "0.5rem",
                  marginBottom: "1rem",
                  width: "100%",
                  textAlign: "center",
                }}
              >
                <p style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CheckCircle size={18} style={{ marginRight: "0.5rem" }} />
                  {message}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ width: "100%", marginTop: "0.5rem" }}>
              {/* Nueva contraseña */}
              <div style={{ marginBottom: "1.25rem" }}>
                <label
                  htmlFor="password"
                  style={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "0.5rem",
                  }}
                >
                  Nueva contraseña
                </label>
                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      position: "absolute",
                      left: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                    }}
                  >
                    <Lock size={20} color="#9ca3af" />
                  </div>

                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      paddingLeft: "2.5rem",
                      paddingRight: "2.5rem",
                      borderRadius: "0.5rem",
                      border: "1px solid #d1d5db",
                      backgroundColor: "#f9fafb",
                    }}
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute",
                      right: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                    }}
                  >
                    {showPassword ? (
                      <EyeOff size={18} color="#6b7280" />
                    ) : (
                      <Eye size={18} color="#6b7280" />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirmar contraseña */}
              <div style={{ marginBottom: "1.5rem" }}>
                <label
                  htmlFor="confirmPassword"
                  style={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "0.5rem",
                  }}
                >
                  Confirmar contraseña
                </label>
                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      position: "absolute",
                      left: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                    }}
                  >
                    <Lock size={20} color="#9ca3af" />
                  </div>

                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      paddingLeft: "2.5rem",
                      paddingRight: "2.5rem",
                      borderRadius: "0.5rem",
                      border: "1px solid #d1d5db",
                      backgroundColor: "#f9fafb",
                    }}
                  />

                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: "absolute",
                      right: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                    }}
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={18} color="#6b7280" />
                    ) : (
                      <Eye size={18} color="#6b7280" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  backgroundColor: "#1e40af",
                  color: "white",
                  padding: "0.75rem 1rem",
                  borderRadius: "0.5rem",
                  fontWeight: "500",
                  fontSize: "0.9rem",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  border: "none",
                  cursor: "pointer",
                  opacity: loading ? 0.75 : 1,
                  marginBottom: "0.75rem",
                }}
              >
                {loading ? "Guardando..." : "Guardar nueva contraseña"}
              </button>

              <button
                type="button"
                onClick={goBackToLogin}
                style={{
                  width: "100%",
                  backgroundColor: "#f3f4f6",
                  color: "#374151",
                  padding: "0.6rem 1rem",
                  borderRadius: "0.5rem",
                  fontSize: "0.85rem",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  border: "none",
                  cursor: "pointer",
                  gap: "0.4rem",
                }}
              >
                <ArrowLeft size={16} />
                Volver al inicio de sesión
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
